import { useRouter } from 'next/router';
import { useGlobalLogo } from '@/quote/hooks/useGlobalLogo';
import { clearAction, updateAction } from '@/quote/util/update-action';
import {
  apiGetCandidate,
  apiGetContractRequirements,
  apiGetValidationInfoWithNoAuth,
  apiGetValidationRequirements,
  getValidation,
} from '@jove-frontend/uikit';
import { useStateMachine } from 'little-state-machine';

const useHandleQuoteFromValidation = () => {
  const { getState, actions } = useStateMachine({ updateAction, clearAction });
  const router = useRouter();
  const { getQuoteChannel } = useGlobalLogo();

  /**
   * requests
   */
  const requestCandidateInfo = async ({ candidate_id, token }) => {
    const [res] = await apiGetCandidate(candidate_id, token);
    return res;
  };

  const requestQuoteValidationInfo = async (data: { validation_id: string }) => {
    const params = { validationId: data?.validation_id, hideErrToast: true };
    const [res] = await apiGetValidationInfoWithNoAuth(params);

    return res;
  };

  const requestQuoteValidationRequirements = async (data: { validation_id: string; token: string }) => {
    const [res] = await apiGetValidationRequirements(data?.validation_id, data?.token);

    return res;
  };

  const requestContractInfo = async ({ contract_id, token }) => {
    const [res] = await apiGetContractRequirements(contract_id, token);

    return res;
  };

  /**
   * methods
   */
  const storeRelatedDateInTheStoreState = (params: {
    candidateData?: any;
    validationData?: any;
    contractData?: any;
    legacyQuoteData?: any;
    assignBusinessInfo?: boolean;
    insuranceType?: string;
    promotionInfo?: any;
    quoteValidationInfo?: any;
  }) => {
    const state = getState();
    const { candidateData, validationData, contractData, legacyQuoteData, assignBusinessInfo, insuranceType, promotionInfo, quoteValidationInfo } =
      params;

    const { token, validation_id, candidate_id, contract_id, org_id, promotion_id, enterQuoteSource } = router.query as any;

    const updateStateData = {
      ...state,
      promotionId: promotion_id,
      meta: {
        ...state.meta,
        token,
        validation_id,
        candidate_id,
        contract_id,
        org_id,
        params: {
          ...state?.meta?.params,
          ...router?.query,
        },
        enterQuoteSource,
      },
    };
    // legacy/quotes data
    if (legacyQuoteData) {
      updateStateData.quote = {
        id: legacyQuoteData?.quote?.id,
        created_at: legacyQuoteData?.quote?.created_at,
        updated_at: legacyQuoteData?.quote?.updated_at,
        viewed_at: legacyQuoteData?.quote?.viewed_at,
        notified: legacyQuoteData?.quote?.notified,
      };
    }

    // contact
    if (candidateData) {
      const { first_name, last_name, email, phone_number, country_code, company_name = '', organization_name } = candidateData;

      updateStateData.contact = {
        ...state.contact,
        first_name,
        last_name,
        email,
        phone_number: !!phone_number && !phone_number.includes('+') ? `+${phone_number}` : phone_number,
        country: country_code,
      };
      updateStateData.company = {
        ...state.company,
        name: company_name,
      };
      updateStateData.organization = {
        ...state.organization,
        organization_name: organization_name,
      };
    }

    // Validation data
    if (validationData) {
      updateStateData.validationRequirements = {
        ...validationData,
      };
    }
    updateStateData.validationInfo = {
      ...state.validationInfo,
      validation: {
        ...state.validationInfo?.validation,
        payload: quoteValidationInfo?.payload,
      },
    };

    // Contract data
    if (contractData) {
      updateStateData.contractRequirements = {
        ...contractData,
      };
    }

    // The original assignment business logic, Keep it temporarily and delete it as required
    if (assignBusinessInfo) {
      updateStateData.business = {
        type: undefined,
        // @ts-ignore
        activities: [],
        turnover: '',
        name: '',
        number: '',
        address: '',
        address2: '',
        postcode: '',
        employees: 0,
        UKOnlyCustomers: false,
        turnoverBreakdownEU: 0,
        turnoverBreakdownOther: 0,
        turnoverBreakdownUK: 0,
        turnoverBreakdownUSA: 0,
      };
    }

    // Insurance type
    updateStateData.insuranceType = insuranceType;

    // Validation start date: The interface is missing data
    // updateStateData.projectPeriodStart = new Date();

    // Channel
    updateStateData.channel = getQuoteChannel();

    if (promotionInfo) {
      updateStateData.promotionInfo = promotionInfo;
    }

    actions.updateAction(updateStateData);
  };

  return {
    requestCandidateInfo,
    requestContractInfo,
    storeRelatedDateInTheStoreState,
    requestQuoteValidationRequirements,
    requestQuoteValidationInfo,
  };
};

export default useHandleQuoteFromValidation;
